import type { SyntheticEvent } from "react";

import type { ChapterBlock } from "../types/VideoTypes";
import {
  NormalizedVideoChapter,
  normalizeVideoChapters,
} from "../storyblok/video/normalizeVideoChapters";

type AnalyticsSession = {
  videoId: number;
  videoTitle: string;
  played: boolean;
  playedSeconds: number;
  lastStartTime: number | null;
  isSeeking?: boolean;
  videoDuration: number;
  normalizedChapters?: NormalizedVideoChapter[] | null;
  playedChapters: string[];
  sent: boolean;
};

/**
 * Creates a new analytics session for a video.
 * This session tracks whether:
 * - the video has been played
 * - how many seconds have been played
 * - the last start time of the video
 * - whether the user is currently seeking in the video
 *
 * @param videoId The ID of the video.
 * @param videoTitle The title of the video.
 *
 * @returns A new analytics session object.
 */
export function createAnalyticsSession(
  videoId: number,
  videoTitle: string
): AnalyticsSession {
  return {
    videoId,
    videoTitle,
    played: false, // Whether the video has been played at least once during this session.
    playedSeconds: 0, // Total number of seconds the video has been played during this session.
    lastStartTime: null, // The timestamp when the video was last started (used to calculate playedSeconds).
    isSeeking: false, // Whether the user is currently seeking in the video.
    videoDuration: 0, // The total duration of the video in seconds.
    normalizedChapters: null, // The list of normalized chapters for the video.
    playedChapters: [], // The list of chapters that have been played during this session.
    sent: false, // Whether the "video" analytics event has been sent for this session yet.
  };
}

/**
 * Handles the play event for a video analytics session.
 * This function updates the session to indicate that the video has been played.
 *
 * @param session The current analytics session for the video.
 * @param event The play event from the video element.
 *
 * @returns The updated analytics session.
 */
export function handleSessionPlay(
  session: AnalyticsSession,
  event: SyntheticEvent<HTMLVideoElement>,
  introductionTitle: string,
  chapters?: ChapterBlock[]
) {
  if (!session.played) {
    session.played = true;
  }

  session.lastStartTime = event.currentTarget.currentTime;
  session.videoDuration = event.currentTarget.duration;

  if (chapters && chapters.length > 0) {
    session.normalizedChapters = normalizeVideoChapters(
      chapters,
      session.videoDuration,
      introductionTitle
    );
  }

  return session;
}

/**
 * Handles the timeupdate event for a video analytics session.
 * This function updates the session to track how many seconds have been played.
 *
 * @param session The current analytics session for the video.
 * @param event The timeupdate event from the video element.
 *
 * @returns The updated analytics session.
 */
export function handleSessionTimeUpdate(
  session: AnalyticsSession,
  event: SyntheticEvent<HTMLVideoElement>
) {
  const currentTime = event.currentTarget.currentTime;

  // If the video is paused, seeking, or the user is currently seeking, we don't want to update the playedSeconds.
  if (
    event.currentTarget.paused ||
    event.currentTarget.seeking ||
    session.isSeeking
  ) {
    session.lastStartTime = null;
    return session;
  }

  // If the video is playing and the user is not seeking, we can update the playedSeconds.
  if (session.lastStartTime !== null) {
    session.playedSeconds += currentTime - session.lastStartTime;
  }
  session.lastStartTime = currentTime;

  // If the session has normalized chapters, we can track which chapters have been played based on the current time.
  if (session.normalizedChapters) {
    let currentChapter: NormalizedVideoChapter | undefined;

    for (
      let chapterIndex = session.normalizedChapters.length - 1;
      chapterIndex >= 0;
      chapterIndex -= 1
    ) {
      if (session.normalizedChapters[chapterIndex].startTime <= currentTime) {
        currentChapter = session.normalizedChapters[chapterIndex];
        break;
      }
    }

    if (
      currentChapter &&
      !session.playedChapters.includes(currentChapter.title)
    ) {
      session.playedChapters.push(currentChapter.title);
    }
  }

  return session;
}

/**
 * Sends the "video" Simple Analytics event summarizing the session, but only
 * once per session, and only if the video was actually played.
 *
 * @param session The current analytics session for the video.
 *
 * @returns The updated analytics session (marked as sent, if applicable).
 */
export function sendVideoAnalyticsEvent(
  session: AnalyticsSession
): AnalyticsSession {
  if (!session.played || session.sent) {
    return session;
  }

  const metadata: Record<string, string | number | boolean> = {
    video_id: session.videoId,
    video_title: session.videoTitle,
    played_seconds: Math.round(session.playedSeconds),
    video_duration: Math.round(session.videoDuration),
    played_chapters: session.playedChapters.join(","),
  };

  if (globalThis.sa_event) {
    globalThis.sa_event("video", metadata);
  }

  session.sent = true;

  return session;
}
